import { getAuthToken, getAuthUid } from '@/services/auth';
import type {
  PointsWebSocketCallbacks,
  PointsWebSocketOptions,
  PointsWebSocketServerMessage,
} from '@/types';

const DEFAULT_OPTIONS: Required<PointsWebSocketOptions> = {
  autoReconnect: true,
  maxReconnectAttempts: 5,
  reconnectIntervalBase: 1000,
  pingInterval: 30000,
  pongTimeout: 10000,
};

function getPointsApiBaseUrl(): string {
  const gateway =
    typeof window !== 'undefined' && window.__BACKEND_GATEWAY__ ? window.__BACKEND_GATEWAY__ : '';
  return gateway ? `${gateway}/api` : '/api';
}

export function buildPointsWebSocketUrl(
  uid: number,
  token: string | null = getAuthToken(),
): string {
  const apiBaseUrl = getPointsApiBaseUrl();
  let baseOrigin: string;
  let apiPath: string;

  if (apiBaseUrl.startsWith('http://') || apiBaseUrl.startsWith('https://')) {
    const url = new URL(apiBaseUrl);
    baseOrigin = url.origin;
    apiPath = url.pathname.replace(/\/$/, '');
  } else {
    baseOrigin = window.location.origin;
    apiPath = apiBaseUrl.replace(/\/$/, '');
  }

  const wsProtocol = baseOrigin.startsWith('https://') ? 'wss:' : 'ws:';
  const host = baseOrigin.replace(/^https?:\/\//, '');
  const query = [`uid=${encodeURIComponent(String(uid))}`];
  if (token) {
    query.push(`token=${encodeURIComponent(token)}`);
  }

  return `${wsProtocol}//${host}${apiPath}/ws/points?${query.join('&')}`;
}

export class PointsWebSocket {
  private ws: WebSocket | null = null;
  private callbacks: PointsWebSocketCallbacks = {};
  private options: Required<PointsWebSocketOptions>;
  private uid: number | null = null;
  private isConnecting = false;
  private isConnected = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private manualClose = false;

  constructor(options: PointsWebSocketOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  connect(): void {
    const uid = getAuthUid();
    if (!uid) {
      this.callbacks.onError?.(new Error('Missing auth uid'));
      return;
    }

    const wsAlive =
      this.ws !== null &&
      (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN);
    if ((this.isConnecting || this.isConnected || wsAlive) && this.uid === uid) {
      return;
    }

    if (this.isConnecting || this.isConnected || wsAlive) {
      this.close();
    }

    this.uid = uid;
    this.manualClose = false;
    this.doConnect();
  }

  setCallbacks(callbacks: PointsWebSocketCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  close(): void {
    this.manualClose = true;
    this.clearReconnectTimer();
    this.stopHeartbeat();
    this.isConnecting = false;
    this.isConnected = false;

    if (
      this.ws &&
      (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)
    ) {
      this.ws.close();
    }
    this.ws = null;
  }

  private doConnect(): void {
    if (!this.uid) {
      this.callbacks.onError?.(new Error('Missing auth uid'));
      return;
    }

    this.isConnecting = true;
    try {
      this.ws = new WebSocket(buildPointsWebSocketUrl(this.uid));
      this.setupEventHandlers();
    } catch (error) {
      this.isConnecting = false;
      this.callbacks.onError?.(error instanceof Error ? error : new Error(String(error)));
      this.scheduleReconnect();
    }
  }

  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.isConnecting = false;
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };

    this.ws.onerror = () => {
      this.callbacks.onError?.(new Error('Points websocket connection error'));
    };

    this.ws.onclose = (event) => {
      this.isConnecting = false;
      this.isConnected = false;
      this.stopHeartbeat();
      this.callbacks.onDisconnected?.(event);
      this.ws = null;

      if (!this.manualClose) {
        this.scheduleReconnect();
      }
    };
  }

  private handleMessage(data: string): void {
    let message: PointsWebSocketServerMessage;
    try {
      message = JSON.parse(data) as PointsWebSocketServerMessage;
    } catch {
      this.callbacks.onError?.(new Error('Invalid points websocket message'));
      return;
    }

    switch (message.type) {
      case 'points_connection_established':
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.callbacks.onConnected?.(message);
        break;
      case 'points_summary_updated':
        this.callbacks.onSummaryUpdated?.(message.data);
        break;
      case 'points_summary_invalidated':
        this.callbacks.onSummaryInvalidated?.();
        break;
      case 'pong':
        this.clearPongTimer();
        break;
      case 'error':
        this.callbacks.onError?.(message);
        break;
      default:
        break;
    }
  }

  private scheduleReconnect(): void {
    if (!this.options.autoReconnect) return;
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) return;

    const attempt = this.reconnectAttempts + 1;
    this.reconnectAttempts = attempt;
    this.callbacks.onReconnecting?.(attempt);
    const delay = this.options.reconnectIntervalBase * Math.pow(2, attempt - 1);
    this.reconnectTimer = setTimeout(() => this.doConnect(), delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.pingTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      this.ws.send(JSON.stringify({ type: 'ping' }));
      this.clearPongTimer();
      this.pongTimer = setTimeout(() => {
        this.ws?.close();
      }, this.options.pongTimeout);
    }, this.options.pingInterval);
  }

  private stopHeartbeat(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    this.clearPongTimer();
  }

  private clearPongTimer(): void {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

export const createPointsWebSocket = (options?: PointsWebSocketOptions): PointsWebSocket =>
  new PointsWebSocket(options);
